<template>
  <div class="user-wrapper" @click="emit('closeUser')">
    <div class="user-profile" @click.stop>
      <OverlayScrollbars :options="scrollbarOptions" class="user-scroller">
        <ArrowLeftIcon @click="emit('closeUser')" 
                        class="close-button"/>
        <img :src="auth.thumbURL" class="user-avatar" v-if="auth.loggedIn"/>
        <span class="user-username">{{auth.username}}</span>
        <section v-if="auth.loggedIn">
          <h4>Account</h4>
          <hr>
          <div class="content">
            <div>Logged In at Feb 1, 2021 12:00:00 AM
              <button class="v-button">See Activity</button>
            </div>
            <button class="v-button">Change Password</button>
            <button class="v-button" @click="signOut">Sign Out</button>
          </div>
        </section>
        <LoginForm v-else 
          @login="handleSignIn"
          :error="auth.errorMessage" >
          <template #otherMethods>
            <div class="external-auth">
              <button class="v-button">
                <img src="https://cdn.iconscout.com/icon/free/png-256/free-google-160-189824.png?f=webp&w=32" alt="Google" />
              </button>
              <button class="v-button">
                <img src="https://cdn.iconscout.com/icon/free/png-256/free-apple-607-434739.png?f=webp&w=32" alt="Apple" />
              </button>
              <button class="v-button">
                <img src="https://cdn.iconscout.com/icon/free/png-512/free-microsoft-26-722716.png?f=webp&w=32" alt="Microsoft" />
              </button>
              <button class="v-button">
                <img src="https://cdn.iconscout.com/icon/free/png-512/free-linkedin-48-189774.png?f=webp&w=32" alt="LinkedIn" />
              </button>
              <button class="v-button">
                <img style="filter: invert(1)" src="https://cdn.iconscout.com/icon/free/png-512/free-twitter-x-9583449-7741112.png?f=webp&w=32" alt="Twitter" />
              </button>
              <button class="v-button">
                <img src="https://cdn.iconscout.com/icon/free/png-512/free-facebook-75-189796.png?f=webp&w=32" alt="Facebook" />
              </button>
            </div>
          </template>
        </LoginForm>
        <section>
          <h4>Preferences</h4>
          <hr>
          <div class="content">
            <div class="themes">
              <label>Theme: </label>
              <div v-for="theme of availableThemes" :data-theme="theme" class="theme-card" @click="setTheme(theme)">
                <input type="radio" name="selected-theme" :id="theme" :value="theme" :checked="theme === selectedTheme">
                <label :for="theme">{{ theme }}</label>
              </div>
            </div>
          </div>
        </section>
      </OverlayScrollbars>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/store/auth';
import { ArrowLeftIcon } from 'vue-tabler-icons';
import { onBeforeMount, ref } from 'vue';
import { useStorage  } from '@vueuse/core';
import { LoginForm } from 'VoyagerComponents';


const {
  getReadOnlyAuth: auth,
  signIn,
  signOut
} = useAuthStore();
const handleSignIn = (payload: {username: string, password: string, rememberMe: boolean}) => {
  signIn(payload.username, payload.password, payload.rememberMe);
};
const emit = defineEmits(['closeUser']);
const availableThemes = ref(['slate', 'light', 'dark']);
const selectedTheme = ref('slate');
const storedTheme = useStorage('preferences', {theme: selectedTheme.value}, localStorage);
const setTheme = (theme?) => {
    if(theme) selectedTheme.value = theme;
    document.documentElement.setAttribute('data-theme', selectedTheme.value);
    storedTheme.value = {theme: selectedTheme.value};
  }

  const scrollbarOptions = {
    scrollbars: {
      theme: 'os-theme-light',
    },
    overflow: {
      x:'hidden'
    }
  };


onBeforeMount(() => {
  const preferences = useStorage('preferences', {theme: 'slate'}, localStorage);
  selectedTheme.value = preferences.value.theme;
})
</script>

<style lang="scss" scoped>
.user-wrapper {
  height: 100%;
  width: 100%;
  background-color: #1e1e1ea6;
  display: flex;
  justify-content: center;
  align-items: center;

  .user-scroller {
    width: 100%;
    height: 100%;
  }
  
  .user-profile {
    background-color: var(--app-background);
    width: clamp(12rem, 100%, 50rem);
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    padding-top:1rem;
    margin-top:1rem;
    margin-bottom: 1rem;
    border-radius: var(--border-radius);
    border-style: solid;
    border-color: #ffffff38;
    overflow-y: auto;
    overflow-x: hidden;
    .close-button {
      position: absolute;
      top: 1rem;
      left: 1rem;
      width: 2rem;
      height: 2rem;
      border-radius: 1rem;
      transition: all 0.25s ease;
      background-color: unset;
      &:hover {
        cursor: pointer;
        background-color: var(--color-background-light);
      }
    }

    .user-username {
      padding-top: 1rem;
      font-size: var(--font-size-large);
    }
  }
  .user-avatar {
    height: 10rem;
    width: 10rem;
    border-radius: 5rem;
  }
}

.section-header {
  width: 100%;
  text-align: left;
  position: relative;
  margin-top: 2rem;
  hr {
    position: absolute;
    top: 0.25rem;
    width: 100%;
  }
  span:first-child {
    left: 1rem;
    position: relative;
    background-color: var(--app-background);
    z-index: 1;
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

section {
  text-align: left;
  position: relative;
  width: 100%;
  hr {
    position: absolute;
    top: 1.5rem;
    width: 100%;
  }
  h4 {
    left: 1rem;
    width: fit-content;
    position: relative;
    background-color: var(--app-background);
    z-index: 1;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .content {
    padding: 1rem;
    padding-top: 0px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    button {
      width: fit-content;
      &:hover {
        cursor: pointer;
        background-color: var(--color-background-light);
      }
    }
  }

  &:last-of-type {
    padding-bottom: 10%;
  }
}

.external-auth {
  display: flex;
  flex-direction: row;
  gap: 0.6rem;
  justify-content: center;

  button {
    width: 3rem;
    height: 3rem;
    border-radius: 3rem;
    &:hover {
      cursor: pointer;
      background-color: var(--color-background-dark);
    }
  }
  
}

.themes {
  display: flex;
  flex-direction: row;
  gap: 1rem;
}
.theme-card {
  background-color: var(--app-background);
  width: 10rem;
  height: 8rem;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  place-content: center;
  border-style: solid;
  border-width: 1px;
  border-color: var(--color-background-dark);
  &:hover {
    cursor: pointer;
    border-color: var(--color-action-background-primary);
  }
  label {
    color: var(--color-text-primary);
    display: block;
  }
  input {
    display: none;
  }
}
</style>